import CloseIcon from '@mui/icons-material/Close';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { SECURITY_FALLBACKS } from '../../../../../../corelogic/domain/constants/displayFallbacks.constants';
import {
  DESCRIPTION_CHAR_LIMIT,
  NAME_CHAR_LIMIT,
} from '../../../../../../corelogic/domain/constants/fieldLimits.constants';
import {
  FrontendTournamentWithDetails,
  TournamentUpsert,
} from '../../../../../../corelogic/domain/models/tournament.model';
import { ValidationResult } from '../../../../../../corelogic/domain/utils/realtimeSecurityValidation.utils';
import { SafeText } from '../../../../../../corelogic/domain/utils/xssProtection.utils';
import { TournamentService } from '../../../services/TournamentService';
import { SecureTextField } from '../../Shared/SecureTextField/SecureTextField';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      bgcolor: 'rgba(20, 20, 20, 0.95)',
      color: '#ffffff',
      borderRadius: 2,
      maxWidth: '700px',
      width: '90%',
      backgroundImage:
        'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7))',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
      border: '1px solid rgba(120, 120, 120, 0.4)',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    bgcolor: 'rgba(0, 0, 0, 0.7)',
    borderBottom: '1px solid rgba(204, 0, 0, 0.3)',
    p: 2,
  },
  title: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  closeButton: {
    color: '#cccccc',
    '&:hover': {
      color: '#ffffff',
      bgcolor: 'rgba(204, 0, 0, 0.2)',
    },
  },
  dialogContent: {
    p: 3,
  },
  formField: {
    mb: 2,
    '& .MuiInputLabel-root': {
      color: '#cccccc',
    },
    '& .MuiOutlinedInput-root': {
      color: '#ffffff',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(204, 0, 0, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    '& .MuiInputBase-input': {
      bgcolor: 'rgba(0, 0, 0, 0.3)',
    },
    '& .MuiSelect-icon': {
      color: '#cccccc',
    },
  },
  sectionTitle: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
    color: '#cc0000',
    mt: 3,
    mb: 2,
    borderLeft: '3px solid #cc0000',
    pl: 1,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: 3,
    gap: 2,
  },
  cancelButton: {
    color: '#cccccc',
    borderColor: '#cccccc',
    '&:hover': {
      borderColor: '#ffffff',
      bgcolor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  saveButton: {
    background: 'linear-gradient(45deg, #990000 30%, #cc0000 90%)',
    color: '#ffffff',
    '&:hover': {
      background: 'linear-gradient(45deg, #770000 30%, #aa0000 90%)',
    },
    boxShadow: '0 3px 5px 2px rgba(204, 0, 0, .3)',
  },
  passwordSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 2,
  },
  lockIcon: {
    color: '#cc0000',
    fontSize: 'small',
  },
  required: {
    color: '#cc0000',
    ml: 0.5,
  },
  characterCount: {
    fontSize: '0.75rem',
    color: '#999999',
    textAlign: 'right',
    mt: 0.5,
  },
  characterCountWarning: {
    color: '#ff9900',
  },
  characterCountError: {
    color: '#ff0000',
  },
  dangerButton: {
    color: '#ffffff',
    backgroundColor: 'rgba(204, 0, 0, 0.7)',
    '&:hover': {
      backgroundColor: 'rgba(204, 0, 0, 0.9)',
    },
    width: '100%',
    mt: 1,
    display: 'flex',
    justifyContent: 'flex-start',
  },
  iconSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
};

interface TournamentSettingsModalProps {
  open: boolean;
  onClose: () => void;
  tournament: FrontendTournamentWithDetails;
  isCreator: boolean;
  onLeaveTournament: (success: boolean) => void;
  onUpsertTournament: (success: boolean) => void;
}

const TournamentSettingsModal = ({
  open,
  onClose,
  tournament,
  isCreator,
  onLeaveTournament,
  onUpsertTournament,
}: TournamentSettingsModalProps) => {
  const [name, setName] = useState(tournament.name);
  const [description, setDescription] = useState(tournament.description || '');
  const [tournamentUpsert, setTournamentUpsert] =
    useState<TournamentUpsert | null>(null);
  const [password, setPassword] = useState('');
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Simplified security validation state
  const [fieldValidations, setFieldValidations] = useState<
    Record<string, ValidationResult>
  >({});

  useEffect(() => {
    setName(tournament.name);
    setDescription(tournament.description || '');
    setTournamentUpsert({
      id: tournament.id,
      name: tournament.name,
      description: tournament.description || '',
      private: tournament.private,
      startDate: tournament.startDate,
      numberOfSeasons: tournament.numberOfSeasons,
    });
  }, [tournament]);

  // Generic secure change handler
  const handleSecureChange = (
    field: string,
    value: string,
    validation: ValidationResult,
  ) => {
    // Update form values
    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'password':
        setPassword(value);
        break;
    }

    // Update validation state
    setFieldValidations((prev) => ({ ...prev, [field]: validation }));
  };

  // Check if there are any high-severity security issues
  const hasSecurityIssues = () => {
    return Object.values(fieldValidations).some((validation) =>
      validation.warnings.some((warning) => warning.severity === 'high'),
    );
  };

  const handleSaveChanges = async () => {
    if (!tournamentUpsert) {
      console.warn('Tournament upsert is null');
      onUpsertTournament(false);
      return;
    }
    //create a new tournament upsert object using the tournament object with the new name and description
    const tournamentToUpsert: TournamentUpsert = {
      id: tournamentUpsert.id,
      name: name,
      description: description,
      password: password ? password : undefined,
      private: tournamentUpsert.private,
      startDate: tournamentUpsert.startDate,
      numberOfSeasons: tournamentUpsert.numberOfSeasons,
    };

    const result = await TournamentService.upsertTournament(
      tournamentToUpsert,
      tournamentUpsert.id,
    );
    if (result) {
      onUpsertTournament(true);
    } else {
      onUpsertTournament(false);
    }
  };

  const handleLeaveTournament = async () => {
    if (confirmLeave) {
      const result = await TournamentService.deleteUserTournament(
        tournament.id,
      );
      if (!result) {
        onLeaveTournament(false);
        cancelLeave();
      } else {
        onLeaveTournament(true);
      }
    } else {
      setConfirmLeave(true);
    }
  };

  const cancelLeave = () => {
    setConfirmLeave(false);
  };

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.title}>
          <SettingsIcon sx={{ color: '#cc0000' }} />
          <SafeText
            text={tournament.name}
            fallback={SECURITY_FALLBACKS.TOURNAMENT_NAME}
          />
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        <Grid container spacing={2}>
          {/* General Settings Section */}
          <Grid item xs={12}>
            <Typography sx={styles.sectionTitle}>General Settings</Typography>
          </Grid>

          {/* Leave Tournament Button */}
          <Grid item xs={12}>
            {confirmLeave ? (
              <Alert
                severity="warning"
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button color="inherit" size="small" onClick={cancelLeave}>
                      Cancel
                    </Button>
                    <Button
                      color="error"
                      size="small"
                      variant="contained"
                      onClick={handleLeaveTournament}
                    >
                      Confirm
                    </Button>
                  </Box>
                }
              >
                Are you sure you want to leave this tournament? You will need to
                rejoin to access it again.
              </Alert>
            ) : (
              <Button
                variant="contained"
                startIcon={<ExitToAppIcon />}
                sx={styles.dangerButton}
                onClick={handleLeaveTournament}
              >
                Leave Tournament
              </Button>
            )}
          </Grid>

          {/* Creator-only settings */}
          {isCreator && (
            <>
              <Grid item xs={12}>
                <Divider
                  sx={{ mt: 2, mb: 2, bgcolor: 'rgba(204, 0, 0, 0.3)' }}
                />
                <Typography sx={styles.sectionTitle}>
                  Creator Settings
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <SecureTextField
                  label="Tournament Name"
                  value={name}
                  onChange={(value, validation) =>
                    handleSecureChange('name', value, validation)
                  }
                  fieldType="name"
                  fieldName="Tournament Name"
                  required
                  fullWidth
                  sx={styles.formField}
                  showCharacterCounter={true}
                  inputProps={{ maxLength: NAME_CHAR_LIMIT }}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={styles.required}>*</Typography>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <SecureTextField
                  label="Description"
                  value={description}
                  onChange={(value, validation) =>
                    handleSecureChange('description', value, validation)
                  }
                  fieldType="description"
                  fieldName="Tournament Description"
                  multiline
                  rows={3}
                  fullWidth
                  sx={styles.formField}
                  placeholder="Describe your tournament (optional)"
                  showCharacterCounter={true}
                  inputProps={{ maxLength: DESCRIPTION_CHAR_LIMIT }}
                />
              </Grid>

              {/* Password section if tournament is private */}
              {tournament.private && (
                <Grid item xs={12}>
                  <Box sx={styles.passwordSection}>
                    <LockIcon sx={styles.lockIcon} />
                    <Typography variant="body2" sx={{ color: '#cccccc' }}>
                      Update tournament password
                    </Typography>
                  </Box>
                  <SecureTextField
                    label="New Password"
                    value={password}
                    onChange={(value, validation) =>
                      handleSecureChange('password', value, validation)
                    }
                    fieldType="tournament_password"
                    fieldName="Tournament Password"
                    fullWidth
                    type="password"
                    sx={styles.formField}
                    placeholder="Leave blank to keep current password"
                    showCharacterCounter={true}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider
                  sx={{ mt: 2, mb: 2, bgcolor: 'rgba(204, 0, 0, 0.3)' }}
                />
              </Grid>
            </>
          )}
        </Grid>

        {/* Action Buttons */}
        <Box sx={styles.buttonContainer}>
          <Button variant="outlined" onClick={onClose} sx={styles.cancelButton}>
            Cancel
          </Button>
          {isCreator && (
            <Button
              onClick={handleSaveChanges}
              variant="contained"
              sx={styles.saveButton}
              disabled={hasSecurityIssues()}
            >
              Save Changes
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TournamentSettingsModal;
